import { Braces } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ObservedMetadataKeyDto, ParameterDto } from '@/Domain/api/Action/dto/Action.dto';
import {
  AvailableVariable,
  BUILTIN_VARIABLES,
  VISITOR_METADATA_VARIABLES,
  isVisitorMetadataVariableName,
} from '@/Domain/components/Action/Lexical/availableVariables';
import {
  DiscordIcon,
  HubspotIcon,
  TelegramIcon,
  type AppIcon,
} from '@/Domain/components/Shared/BrandIcon/BrandIcon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import { ParameterTypeEnum } from '@/Domain/enum/ParameterType.enum';
import { useObservedMetadataKeys } from '@/Domain/hooks/Action/useObservedMetadataKeys.hook';
import { useDiscordTokenStatus } from '@/Domain/hooks/Integration/useDiscordTokenStatus.hook';
import { useHubSpotTokenStatus } from '@/Domain/hooks/Integration/useHubSpotTokenStatus.hook';
import { SCENARIO_ID } from '@/context';

type InlineExampleItem = {
  option: ParameterDto;
  example: string | null;
  // True when `example` is the live value (renders verbatim); false when it's a sample (renders with an "e.g." prefix).
  exampleIsActual: boolean;
};

/**
 * Enum for external system access tokens that can be injected at runtime.
 * These tokens are injected into AI Action requests via LangGraph state
 * and are never exposed to the LLM directly.
 *
 * To add a new system token:
 * 1. Add a new entry to this enum
 * 2. Create a corresponding system token object (like HUBSPOT_SYSTEM_TOKEN)
 * 3. Add the token to the systemTokens array based on feature flag
 */
export enum ExternalAccessToken {
  HUBSPOT = 'hubspot_access_token',
  TELEGRAM = 'telegram_bot_token',
  DISCORD = 'discord_bot_token',
}

export const HUBSPOT_ACCESS_TOKEN_PLACEHOLDER = ExternalAccessToken.HUBSPOT;
export const TELEGRAM_BOT_TOKEN_PLACEHOLDER = ExternalAccessToken.TELEGRAM;
export const DISCORD_BOT_TOKEN_PLACEHOLDER = ExternalAccessToken.DISCORD;

const HUBSPOT_SYSTEM_TOKEN: ParameterDto = {
  id: -1,
  name: HUBSPOT_ACCESS_TOKEN_PLACEHOLDER,
  description: 'HubSpot OAuth access token (auto-refreshed)',
  type: ParameterTypeEnum.String,
  default_value: '',
  is_required: false,
};

const TELEGRAM_SYSTEM_TOKEN: ParameterDto = {
  id: -1,
  name: TELEGRAM_BOT_TOKEN_PLACEHOLDER,
  description: 'Telegram bot token, injected into the Bot API URL (never seen by the AI)',
  type: ParameterTypeEnum.String,
  default_value: '',
  is_required: false,
};

const DISCORD_SYSTEM_TOKEN: ParameterDto = {
  id: -1,
  name: DISCORD_BOT_TOKEN_PLACEHOLDER,
  description: 'Discord bot token, injected as the Authorization: Bot header value',
  type: ParameterTypeEnum.String,
  default_value: '',
  is_required: false,
};

// System tokens all share id=-1 (synthetic), so key by placeholder name instead of id.
const SYSTEM_TOKEN_ICON: Record<string, AppIcon> = {
  [ExternalAccessToken.HUBSPOT]: HubspotIcon,
  [ExternalAccessToken.TELEGRAM]: TelegramIcon,
  [ExternalAccessToken.DISCORD]: DiscordIcon,
};

// Channel-set metadata keys carry their channel's icon (like system tokens), so a
// reader can tell at a glance where a value comes from. Matched by `metadata_<prefix>`.
const METADATA_CHANNEL_ICON: { prefix: string; icon: AppIcon }[] = [
  { prefix: 'metadata_telegram_', icon: TelegramIcon },
];

const getMetadataChannelIcon = (name: string): AppIcon | null =>
  METADATA_CHANNEL_ICON.find((c) => name.startsWith(c.prefix))?.icon ?? null;

// id=-1 makes `BadgeNode.exportJSON` round-trip `parameter_id` as null for synthetic items, matching HubSpot system-token behaviour.
const toParameterDto = (variable: AvailableVariable): ParameterDto => ({
  id: -1,
  name: variable.name,
  description: variable.description,
  type: ParameterTypeEnum.String,
  default_value: '',
  is_required: false,
});

type DropdownProps = {
  parameters: ParameterDto[];
  onSelect: (option: ParameterDto) => void;
  // When set, show only the conversation-metadata sections (visitor + observed keys) behind a
  // compact icon trigger. Used by run-conditions, which gate on metadata keys, not on parameters,
  // built-in variables, or system tokens.
  onlyMetadataKeys?: boolean;
};

export const ParameterDropdown = ({
  parameters,
  onSelect,
  onlyMetadataKeys = false,
}: DropdownProps) => {
  const { t } = useTranslation('actions_page');

  const { data: hubSpotTokenStatus } = useHubSpotTokenStatus();
  const { hasToken: hasDiscordToken } = useDiscordTokenStatus();
  const { data: observedMetadataKeys } = useObservedMetadataKeys();

  const params = parameters.filter((param) => param.name);

  const builtinItems = useMemo<InlineExampleItem[]>(
    () =>
      BUILTIN_VARIABLES.map((variable) => {
        const isScenarioId = variable.name === 'scenario_id';
        return {
          option: toParameterDto(variable),
          example: isScenarioId ? SCENARIO_ID || null : (variable.exampleValue ?? null),
          exampleIsActual: isScenarioId,
        };
      }),
    [],
  );

  // Visitor section always renders the four auto-captured fields; observed-keys filters them out below to avoid double-listing.
  const visitorMetadataItems = useMemo(() => VISITOR_METADATA_VARIABLES.map(toParameterDto), []);

  const observedMetadataItems = useMemo<Array<{ option: ParameterDto; example: string | null }>>(
    () =>
      (observedMetadataKeys ?? [])
        .filter((entry: ObservedMetadataKeyDto) => entry.key)
        .filter(
          (entry: ObservedMetadataKeyDto) =>
            !isVisitorMetadataVariableName(`metadata_${entry.key}`),
        )
        .map((entry: ObservedMetadataKeyDto) => ({
          option: toParameterDto({
            name: `metadata_${entry.key}`,
            description: t(
              'sheet.form.data_to_collect.dropdown.metadata_observed_description',
              'A value seen in recent conversations, set by the chat channel, an integration, or our API.',
            ),
          }),
          example: entry.example_value,
        })),
    [observedMetadataKeys, t],
  );

  const systemTokens = useMemo(() => {
    const tokens: ParameterDto[] = [];
    if (hubSpotTokenStatus?.has_token) {
      tokens.push(HUBSPOT_SYSTEM_TOKEN);
    }
    // Surface the Telegram bot token once the scenario has Telegram activity
    // (telegram_* keys observed), keeping it out of non-Telegram scenarios.
    if (observedMetadataKeys?.some((entry) => entry.key?.startsWith('telegram_'))) {
      tokens.push(TELEGRAM_SYSTEM_TOKEN);
    }
    if (hasDiscordToken) {
      tokens.push(DISCORD_SYSTEM_TOKEN);
    }
    return tokens;
  }, [hubSpotTokenStatus?.has_token, observedMetadataKeys, hasDiscordToken]);

  const isEmpty = onlyMetadataKeys
    ? observedMetadataItems.length === 0 && visitorMetadataItems.length === 0
    : params.length === 0 &&
      builtinItems.length === 0 &&
      observedMetadataItems.length === 0 &&
      visitorMetadataItems.length === 0 &&
      systemTokens.length === 0;

  const sectionHeader = (label: string, testId: string, hint?: string) => (
    <DropdownMenuLabel data-testid={testId}>
      <p>{label}</p>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </DropdownMenuLabel>
  );

  const sectionDivider = (key: string) => <DropdownMenuSeparator key={key} />;

  const renderSection = (
    key: string,
    headerLabel: string,
    headerTestId: string,
    items: ParameterDto[],
    itemPrefix: string,
    addDividerAbove: boolean,
  ) => {
    if (items.length === 0) return null;
    return (
      <DropdownMenuGroup key={key}>
        {addDividerAbove && sectionDivider(`${key}-divider`)}
        {sectionHeader(headerLabel, headerTestId)}
        {items.map((option) => (
          <DropdownMenuItem
            key={`${itemPrefix}-${option.name}`}
            onSelect={() => onSelect(option)}
            className="whitespace-normal [overflow-wrap:anywhere]"
            title={option.description}
            textValue={option.name}
            data-testid={`${itemPrefix}-${option.name}`}
          >
            {option.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuGroup>
    );
  };

  const renderInlineExample = (example: string, exampleIsActual: boolean) => (
    <span className="ml-auto text-xs text-muted-foreground italic truncate max-w-[20ch]">
      {exampleIsActual
        ? example
        : t('sheet.form.data_to_collect.dropdown.metadata_example_inline', 'e.g. {{value}}', {
            value: example,
          })}
    </span>
  );

  const renderObservedSection = (addDividerAbove: boolean) => {
    if (observedMetadataItems.length === 0) return null;
    return (
      <DropdownMenuGroup key="metadata-observed">
        {addDividerAbove && sectionDivider('metadata-observed-divider')}
        {sectionHeader(
          t(
            'sheet.form.data_to_collect.dropdown.metadata_observed',
            'Metadata (used in recent conversations)',
          ),
          'metadata-observed-section-header',
          t(
            'sheet.form.data_to_collect.dropdown.metadata_observed_hint',
            'Custom values sent in past conversations (widget, integrations, or our API)',
          ),
        )}
        {observedMetadataItems.map(({ option, example }) => {
          const ChannelIcon = getMetadataChannelIcon(option.name);
          return (
            <DropdownMenuItem
              key={`metadata-observed-${option.name}`}
              onSelect={() => onSelect(option)}
              title={option.description}
              textValue={option.name}
              data-testid={`metadata-observed-item-${option.name}`}
            >
              {ChannelIcon && <ChannelIcon />}
              <span className="min-w-0 whitespace-normal [overflow-wrap:anywhere]">
                {option.name}
              </span>
              {example && renderInlineExample(example, false)}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuGroup>
    );
  };

  const renderVisitorSection = (addDividerAbove: boolean) => {
    if (visitorMetadataItems.length === 0) return null;
    return (
      <DropdownMenuGroup key="metadata-visitor">
        {addDividerAbove && sectionDivider('metadata-visitor-divider')}
        {sectionHeader(
          t('sheet.form.data_to_collect.dropdown.metadata_visitor', 'Visitor'),
          'metadata-visitor-section-header',
          t(
            'sheet.form.data_to_collect.dropdown.metadata_visitor_hint',
            'Auto-captured from the chat channel, integration, your widget, or API',
          ),
        )}
        {visitorMetadataItems.map((option) => (
          <DropdownMenuItem
            key={`metadata-visitor-${option.name}`}
            onSelect={() => onSelect(option)}
            className="whitespace-normal [overflow-wrap:anywhere]"
            title={option.description}
            textValue={option.name}
            data-testid={`metadata-visitor-item-${option.name}`}
          >
            {option.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuGroup>
    );
  };

  const renderBuiltinSection = (addDividerAbove: boolean) => {
    if (builtinItems.length === 0) return null;
    return (
      <DropdownMenuGroup key="builtin">
        {addDividerAbove && sectionDivider('builtin-divider')}
        {sectionHeader(
          t('sheet.form.data_to_collect.dropdown.builtin', 'Built-in'),
          'builtin-section-header',
        )}
        {builtinItems.map(({ option, example, exampleIsActual }) => (
          <DropdownMenuItem
            key={`builtin-item-${option.name}`}
            onSelect={() => onSelect(option)}
            title={option.description}
            textValue={option.name}
            data-testid={`builtin-item-${option.name}`}
          >
            <span className="truncate">{option.name}</span>
            {example && renderInlineExample(example, exampleIsActual)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuGroup>
    );
  };

  const renderMenu = () => {
    const sections: React.ReactNode[] = [];

    if (onlyMetadataKeys) {
      sections.push(renderVisitorSection(false));
      sections.push(renderObservedSection(sections.some(Boolean)));
    } else {
      sections.push(
        renderSection(
          'parameters',
          t('sheet.form.data_to_collect.dropdown.parameters', 'Parameters'),
          'parameters-section-header',
          params,
          'parameter-item',
          false,
        ),
      );

      sections.push(renderVisitorSection(sections.some(Boolean)));

      sections.push(renderObservedSection(sections.some(Boolean)));

      sections.push(renderBuiltinSection(sections.some(Boolean)));
    }

    sections.push(
      !onlyMetadataKeys && systemTokens.length > 0 && (
        <DropdownMenuGroup key="system-tokens">
          {sections.some(Boolean) && sectionDivider('system-tokens-divider')}
          {sectionHeader(
            t('sheet.form.data_to_collect.dropdown.system_tokens', 'System Tokens'),
            'system-tokens-section-header',
          )}
          {systemTokens.map((token) => {
            const TokenIcon = SYSTEM_TOKEN_ICON[token.name] ?? Braces;
            return (
              <DropdownMenuItem
                key={`system-token-${token.name}`}
                onSelect={() => onSelect(token)}
                title={token.description}
                textValue={token.name}
                data-testid={`system-token-${token.name}`}
              >
                <TokenIcon />
                {token.name}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuGroup>
      ),
    );

    return (
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        collisionPadding={8}
        onCloseAutoFocus={(event) => {
          // Keep the editor focus restored by insertion while the menu's exit animation runs.
          if (document.activeElement?.closest('[contenteditable="true"]')) {
            event.preventDefault();
          }
        }}
        className="box-border flex w-96 max-w-[calc(100vw-1rem)] max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] flex-col gap-0.5 overflow-y-auto overscroll-contain p-2"
      >
        {isEmpty ? (
          <p
            className="text-xs whitespace-nowrap text-foreground-subtle"
            data-testid="parameter-dropdown-empty"
          >
            {t('sheet.form.data_to_collect.dropdown.empty')}
          </p>
        ) : (
          sections
        )}
      </DropdownMenuContent>
    );
  };

  return (
    <div className="relative flex items-center justify-center">
      <DropdownMenu modal>
        <DropdownMenuTrigger
          className={
            onlyMetadataKeys
              ? 'text-primary-900 inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg bg-primary-25'
              : 'text-primary-900 inline-flex cursor-pointer justify-between items-center text-sm px-2 py-1 w-full bg-primary-25 rounded-lg'
          }
          aria-label={
            onlyMetadataKeys
              ? t('sheet.form.data_to_collect.dropdown.pick_metadata_key', 'Pick a metadata key')
              : undefined
          }
          title={
            onlyMetadataKeys
              ? t('sheet.form.data_to_collect.dropdown.pick_metadata_key', 'Pick a metadata key')
              : undefined
          }
          data-testid="add-ai-data-button"
        >
          <Braces className="h-4 w-4" />
          {!onlyMetadataKeys && (
            <span className="text-xs font-medium ml-1">
              {t('sheet.form.data_to_collect.dropdown.trigger', 'Add AI Data')}
            </span>
          )}
        </DropdownMenuTrigger>

        {renderMenu()}
      </DropdownMenu>
    </div>
  );
};
